import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  DialogPlugin,
  Dropdown,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
} from 'tdesign-react';
import { connect } from 'react-redux';
import useTable from '@/hooks/useTable';
import { cloneVariableStencil, deleteVariableStencil } from '@/services/variableService';
import { renderDateTime } from '@/utils/common';
import SvgIcon from '@/components/SvgIcon';
import { fetchVariableStencilList } from '@/slices/variableSlice';
import PermWrapper from '@/components/PermWrapper';
import BuriedPermWrapper from '@/components/BuriedWrapper/BuriedPermWrapper';
import { doBuried } from '@/services/buriedService';
import Modal from './Modal';
import VarsModal from './VarsModal';

const options = [
  {
    value: 'edit',
    content: <Link hover='color'>修改</Link>,
  },
  {
    value: 'modify',
    content: <Link hover='color'>调整变量</Link>,
  },
  {
    value: 'delete',
    content: (
      <Link theme='danger' hover='color'>
        删除
      </Link>
    ),
  },
];

function Template({ scopeType, fetchVariableStencilList, stencilList }) {
  const [detail, setDetail] = useState(false);
  const [visible, setVisible] = useState(false);
  const [varVisible, setVarVisible] = useState(false);

  const EVENTS = useMemo(() => {
    switch (scopeType) {
      // 主机变量
      case 0:
        return {
          CLONE: 'TEMPLATE_DETAIL_VARIABLE_HOST_CLONE', // 点击克隆按钮
          CLONSE_SUBMIT: 'TEMPLATE_DETAIL_VARIABLE_HOST_CLONE_SUBMIT', // 点击克隆后确认
        };
      // 容器变量
      case 1:
        return {
          CLONE: 'TEMPLATE_DETAIL_VARIABLE_CONTAINER_CLONE',
          CLONSE_SUBMIT: 'TEMPLATE_DETAIL_VARIABLE_CONTAINER_CLONE_SUBMIT',
        };
      default:
        return;
    }
  }, [scopeType]);

  const linkClick = (eventId) => {
    doBuried({ eventId });
  };

  const columns = [
    {
      title: '默认值模板名称',
      colKey: 'stencilName',
      fixed: 'left',
    },
    {
      title: '说明',
      colKey: 'remark',
      ellipsis: true,
    },
    {
      title: '引用集群数',
      colKey: 'usedCount',
    },
    {
      title: '创建时间',
      colKey: 'createTime',
      cell: renderDateTime,
    },
    {
      title: '更新时间',
      colKey: 'updateTime',
      cell: renderDateTime,
    },
    {
      colKey: 'option',
      title: '操作',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <BuriedPermWrapper
            resource='pulsar:stencil:variables:templateClone'
            eventId={EVENTS.CLONSE_SUBMIT}
            triggerEvent='onConfirm'
          >
            <Popconfirm content='确定克隆？' onConfirm={() => handleClone(row.id)}>
              <Link theme='primary' hover='color' onClick={() => linkClick(EVENTS.CLONE)}>
                克隆
              </Link>
            </Popconfirm>
          </BuriedPermWrapper>
          <PermWrapper resource='pulsar:stencil:variables:templateConfig'>
            <Dropdown
              options={row.stencilName === 'default' ? options.slice(0, -1) : options}
              onClick={({ value }) => onSelect(value, row)}
            >
              <div>
                <Icon type='dots-vertical' />
              </div>
            </Dropdown>
          </PermWrapper>
        </Space>
      ),
      width: 120,
    },
  ];

  const onSelect = (value, row) => {
    switch (value) {
      case 'delete': // 删除
        handleDel(row);
        break;
      case 'modify': // 修改变量
        setDetail(row);
        setVarVisible(true);
        break;
      case 'edit': // 修改
        setDetail(row);
        setVisible(true);
        break;
      default:
        break;
    }
  };

  const handleDel = async (row) => {
    const dialog = DialogPlugin.confirm({
      header: '提示',
      theme: 'warning',
      body: '确定删除？',
      confirmBtn: {
        content: '确定',
      },
      onClose: () => dialog.hide(),
      onConfirm: async () => {
        try {
          dialog.update({
            confirmBtn: { loading: true, content: '确定' },
          });
          await deleteVariableStencil(row.id);
          MessagePlugin.success('操作成功！');
          dialog.hide();
          doRefresh();
        } catch (error) {
          console.trace(error);
          throw error;
        } finally {
          dialog.update({
            confirmBtn: { loading: false, content: '确定' },
          });
        }
      },
    });
  };

  const handleClone = async (id) => {
    await cloneVariableStencil(id);
    doRefresh();
  };

  const { config, header, setData, doSearch, doRefresh } = useTable({
    columns,
    columnController: null,
    refresh: null,
    onSearch: (params) => fetchVariableStencilList({ ...params, scopeType }),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch, scopeType]);

  useEffect(() => {
    setData(stencilList);
  }, [stencilList, setData]);

  const handleClose = (refresh) => {
    setVisible(false);
    setDetail(null);
    setVarVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Modal visible={visible} detail={detail} handleClose={handleClose} />
      <VarsModal visible={varVisible} id={detail?.id} handleClose={handleClose} />
    </Root>
  );
}

export default connect(
  (state) => ({
    stencilList: state.variable.stencilList,
  }),
  {
    fetchVariableStencilList,
  },
)(Template);

const Root = styled.div``;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;
